import { Box, Stack, styled, Theme, Typography, useTheme } from '@mui/material';
import { BoltOutlined } from '@mui/icons-material';
import { BlankModal } from 'sharedComponents/Modal';
import { PrimaryButton, CloseButton } from 'sharedComponents/Buttons';
import { QuickApplyDistrictJobboardRedirectModalProps } from '../../types';
import { ConnectQuickApplyDistrictJobboardRedirectModalDataTestIds } from 'data-testids/ConnectDataTestIds';
import { useConnectContext } from 'features/Connect/features/Context/ConnectContextProvider';
import { useHistory } from 'react-router-dom';
import { buildUrl } from 'utils/util';
import { ConnectGatedActions, ConnectModals } from 'features/Connect/utils/connectEnums';
import { checkUserProfileCompletion } from 'features/Connect/features/ExpressInterest/utils';
import { CONNECT_JOBBOARD_STATES } from 'utils/constants/connect';
import { connectRedirectUrls, connectUrlParams } from 'utils/constants/urls';
import { useStateParam } from 'features/Connect/features/Context/ConnectStateCodeContextProvider';
import auth from 'utils/auth';

export const QuickApplyDistrictJobboardRedirectModal: React.FC<
  QuickApplyDistrictJobboardRedirectModalProps
> = ({ isOpen, onClose, school }) => {
  const theme = useTheme();
  const { connectContextActions } = useConnectContext();
  const history = useHistory();
  const stateCode = useStateParam();
  const user = auth.getUser();
  // Get district slug from context if available
  const districtSlug = school?.district?.nces_id
    ? connectContextActions.getDistrictSlug(school.district.nces_id)
    : undefined;

  if (!school) return null;

  const hasExpressedInterest = () => {
    return (
      connectContextActions.hasSchoolBeenContacted(school.nces_id) || school.hasExpressedInterest
    );
  };

  const handleQuickApply = (): void => {
    connectContextActions.setModalOpen(
      ConnectModals.QUICK_APPLY_DISTRICT_JOBBOARD_REDIRECT_MODAL,
      false
    );

    // Open the specific district's jobboard in a new tab
    window.open(`/jobs/${districtSlug}`, '_blank', 'noopener,noreferrer');
  };

  const expressInterestOnClickHandler = async (): Promise<void> => {
    if (!school) return;

    const urlParamsToAdd = {
      [connectUrlParams.REDIRECT]: CONNECT_JOBBOARD_STATES[stateCode]?.urlParam,
      [connectUrlParams.SCHOOL]: school.nces_id,
      [connectUrlParams.ACTION]: ConnectGatedActions.EXPRESS_INTEREST,
    };

    if (!user) {
      history.push(buildUrl(connectRedirectUrls.SIGN_UP, urlParamsToAdd));
    } else if (!user?.preference?.nimble_connect_profile?.is_completed) {
      if (
        !checkUserProfileCompletion(
          user,
          history,
          stateCode,
          school,
          ConnectGatedActions.EXPRESS_INTEREST
        )
      ) {
        return;
      }
    } else {
      connectContextActions.setModalOpen(
        ConnectModals.QUICK_APPLY_DISTRICT_JOBBOARD_REDIRECT_MODAL,
        false
      );
      connectContextActions.setModalOpen(ConnectModals.EXPRESS_INTEREST_MODAL, true);
      connectContextActions.setSelectedSchool(school);
    }
  };

  const modalHeader = (
    <ModalHeaderContainer>
      <ModalHeaderText
        data-testid={ConnectQuickApplyDistrictJobboardRedirectModalDataTestIds.HEADER_TEXT}
      >
        Good news! This school uses Nimble ATS.
      </ModalHeaderText>
      <CloseButton onClick={onClose} />
    </ModalHeaderContainer>
  );

  const modalBody = (
    <ModalBodyContainer>
      <InfoBox>
        <InfoBoxTitle>You can use your Nimble profile to apply in just a few clicks.</InfoBoxTitle>
        <BenefitsList>
          <BenefitItem>
            •&nbsp;&nbsp;Use one profile across all Nimble Connect & ATS jobs
          </BenefitItem>
          <BenefitItem>
            •&nbsp;&nbsp;Apply at least 2x faster to this district&apos;s roles
          </BenefitItem>
          <BenefitItem>
            •&nbsp;&nbsp;Seamless experience – we&apos;ll guide you directly to the district&apos;s
            job board
          </BenefitItem>
        </BenefitsList>
      </InfoBox>

      <Stack direction="column" width="100%" gap={theme.spacing(2)}>
        <PrimaryButton
          dataTestId={ConnectQuickApplyDistrictJobboardRedirectModalDataTestIds.QUICK_APPLY_BUTTON}
          size="medium"
          onClick={handleQuickApply}
          disabled={!districtSlug}
          sx={{ ...QuickApplyButtonStyle(theme), width: '100%' }}
          startIcon={<BoltOutlined />}
        >
          Quick Apply
        </PrimaryButton>

        {!hasExpressedInterest() && (
          <ExpressInterestText>
            Not ready to apply?{' '}
            <ExpressInterestLink
              data-testid={
                ConnectQuickApplyDistrictJobboardRedirectModalDataTestIds.EXPRESS_INTEREST_LINK
              }
              onClick={expressInterestOnClickHandler}
            >
              Express Interest
            </ExpressInterestLink>
            &nbsp;instead.
          </ExpressInterestText>
        )}
      </Stack>
    </ModalBodyContainer>
  );

  return (
    <BlankModal
      dataTestId={ConnectQuickApplyDistrictJobboardRedirectModalDataTestIds.MODAL}
      header={modalHeader}
      body={modalBody}
      open={isOpen}
      onClose={onClose}
      sx={ModalStyles(theme)}
    />
  );
};

const ModalStyles = (theme: Theme) => ({
  display: 'flex',
  borderRadius: theme.shape.borderRadius,
  maxWidth: '640px',
  margin: 'auto',
  padding: theme.spacing(3, 3),
});

const QuickApplyButtonStyle = (theme: Theme) => ({
  width: '100%',
  height: '100%',
  minHeight: '42px',
  backgroundColor: theme.palette.purple.dark,
  borderRadius: theme.shape.borderRadius * 0.5,
  fontSize: theme.typography.h3.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightMediumBold,
  lineHeight: '20px',
  textWrap: 'nowrap',
  '&:hover': {
    backgroundColor: theme.palette.purple.main,
  },
});

const ModalHeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),

  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start',
  },
}));

const ModalHeaderText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Open Sans',
  color: theme.palette.text.primary,
  fontSize: '18px',
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightMediumBold,
  lineHeight: '28px',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  '&::after': {
    content: '""',
    display: 'inline',
  },
}));

const ModalBodyContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const InfoBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
}));

const InfoBoxTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Open Sans',
  color: theme.palette.text.secondary,
  fontSize: theme.typography.h3.fontSize,
  fontWeight: theme.typography.fontWeightMediumBold,
  lineHeight: '28px',
}));

const BenefitsList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1, 0, 0, 1),
}));

const BenefitItem = styled(Typography)(({ theme }) => ({
  fontFamily: 'Open Sans',
  color: theme.palette.text.light,
  fontSize: '16px',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: '28px',
  paddingLeft: theme.spacing(2),
  textIndent: `-${theme.spacing(1.75)}`,
}));

const ExpressInterestText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.body2.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: '16px',
  textAlign: 'center',
}));

const ExpressInterestLink = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: theme.typography.fontWeightMedium,
  cursor: 'pointer',
  textDecoration: 'underline',
  '&:hover': {
    color: theme.palette.primary.dark,
  },
}));
